import { useState } from "react";
import { useAnnounce } from "../lib/announce";
import { usePreferences } from "../lib/format";
import type { Preferences } from "../lib/preferences";
import { Button, Card, DataTable, EmptyState, ErrorState, LoadingState, PageHeader, RelativeTime, StatusPill, TextField } from "../components/ui";

interface MatrixRow {
  row: number;
  technology: string;
  platform: string;
  browser: string;
  walkedAt: string;
}

/** accessibility-plan.md §2, the rows a walk can use as data. */
const MATRIX: MatrixRow[] = [
  { row: 1, technology: "JAWS", platform: "Windows", browser: "Chrome", walkedAt: "every round" },
  { row: 2, technology: "NVDA", platform: "Windows", browser: "Firefox", walkedAt: "every round, every user-facing PR" },
  { row: 3, technology: "VoiceOver", platform: "macOS", browser: "Safari", walkedAt: "every round" },
  { row: 4, technology: "VoiceOver", platform: "iOS", browser: "Safari", walkedAt: "every round, every phone flow PR" },
  { row: 5, technology: "TalkBack", platform: "Android", browser: "Chrome", walkedAt: "layer 3 until an Android device exists" },
  { row: 6, technology: "ZoomText at 400 %", platform: "Windows", browser: "Edge", walkedAt: "every round" },
  { row: 8, technology: "Dragon Professional", platform: "Windows", browser: "Chrome", walkedAt: "every round" },
  { row: 12, technology: "Keyboard only", platform: "all", browser: "all four", walkedAt: "every PR" },
];

/**
 * Every primitive with sample data, on one page, so the layer-2 walk of
 * the empty shell (accessibility-plan.md §4, PR 2's gate) has something to
 * walk: a table to sort and filter, a field that errors, a button that
 * announces, the three states, a status in words, a relative time, and
 * the preference switches so the walker can change theme, text size,
 * contrast and motion without leaving the page. Not in prod builds.
 */
export function PrimitivesPage() {
  const announce = useAnnounce();
  const { preferences, update } = usePreferences();
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const error = submitted && name.trim().length < 2 ? "Enter at least two characters." : undefined;
  const [count, setCount] = useState(0);
  const [fiveMinutesAgo] = useState(() => new Date(Date.now() - 5 * 60_000).toISOString());

  return (
    <>
      <PageHeader eyebrow={<StatusPill tone="accent">Internal</StatusPill>} title="Primitives" subtitle="A workbench, not a feature: every building block the product is made from, on one page." actions={<Button variant="primary" onClick={() => { setCount((c) => c + 1); announce(`Counted ${count + 1}`); }}>Announce a count</Button>} />

      {/* What the page is for, in the reader's words — the owner asked and the page did not say (owner, 2026-09-22). */}
      <p className="mb-6 max-w-[75ch] text-muted">
        Every screen in this product is assembled from the same small set of parts: a table you can sort and filter, a field that refuses bad input, a button that announces what it did, the loading and empty and error states, a status in
        words, a time in your own format, and the preference switches. They are all here at once, with made-up data, so a screen-reader, magnifier, voice or keyboard walk can test the parts in a single pass instead of hunting for a table
        on one screen and an error message on another — and so a part that works here is known to work everywhere it is used. Nothing here is real, nothing here is saved, and the page is not in the production build.
      </p>

      {/* min-w-0 on each card: a grid track cannot shrink below its widest item, so anything unbreakable inside would widen the page (ACC-07). */}
      <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
        <Card title="Preferences">
          <div className="grid gap-4 sm:grid-cols-2">
            <Select label="Theme" value={preferences.theme} onChange={(v) => update({ theme: v as Preferences["theme"] })} options={["system", "light", "dark"]} />
            <Select label="Text size" value={preferences.textSize} onChange={(v) => update({ textSize: v as Preferences["textSize"] })} options={["default", "large", "x-large"]} />
            <Select label="Contrast" value={preferences.contrast} onChange={(v) => update({ contrast: v as Preferences["contrast"] })} options={["auto", "default", "more"]} />
            <Select label="Motion" value={preferences.motion} onChange={(v) => update({ motion: v as Preferences["motion"] })} options={["auto", "standard", "reduced"]} />
            <Select label="Date format" value={preferences.dateFormat} onChange={(v) => update({ dateFormat: v as Preferences["dateFormat"] })} options={["auto", "iso", "us", "eu"]} />
          </div>
        </Card>

        <Card title="A form">
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              setSubmitted(true);
              if (name.trim().length >= 2) announce(`Saved ${name.trim()}`);
            }}
            className="space-y-4"
          >
            <TextField label="Display name" description="How your name appears to your mentor." value={name} onChange={(e) => setName(e.target.value)} error={error} autoComplete="nickname" />
            <div className="flex flex-wrap gap-3">
              <Button type="submit" variant="primary">
                Save
              </Button>
              <Button onClick={() => { setName(""); setSubmitted(false); }}>Clear</Button>
              <Button variant="danger" onClick={() => announce("Nothing was deleted; this is a sample.", "assertive")}>
                Delete (sample)
              </Button>
            </div>
          </form>
        </Card>

        <Card title="States">
          <div className="space-y-4">
            <LoadingState label="Loading your hours…" />
            <ErrorState title="Your hours could not be loaded." error={{ message: "The server did not answer in time.", requestId: "01J9Z0EXAMPLE" }} onRetry={() => announce("Trying again")} />
            <EmptyState title="No hours logged this week" hint="Log your first entry from Home." />
          </div>
        </Card>

        <Card title="Status and time">
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill tone="ok">Approved</StatusPill>
            <StatusPill tone="warn">Waiting for your mentor</StatusPill>
            <StatusPill tone="danger">Rejected</StatusPill>
            <StatusPill>Draft</StatusPill>
          </div>
          <dl className="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 text-base sm:grid-cols-[auto_1fr]">
            <dt className="text-muted">Submitted</dt>
            <dd>
              <RelativeTime iso={fiveMinutesAgo} />
            </dd>
            <dt className="text-muted">Approved</dt>
            <dd>
              <RelativeTime iso={null} />
            </dd>
          </dl>
        </Card>
      </div>

      <Card className="mt-6" padding="sm" title="A table">
        <DataTable
          caption="Support matrix rows"
          rows={MATRIX}
          rowKey={(r) => String(r.row)}
          empty={<EmptyState title="No rows" />}
          initialSort={{ key: "row", dir: "ascending" }}
          columns={[
            { key: "row", header: "Row", render: (r) => r.row, numeric: true, sortValue: (r) => r.row },
            { key: "technology", header: "Assistive technology", render: (r) => r.technology, sortValue: (r) => r.technology },
            { key: "platform", header: "Platform", render: (r) => r.platform, sortValue: (r) => r.platform },
            { key: "browser", header: "Browser", render: (r) => r.browser },
            { key: "walkedAt", header: "Walked at", render: (r) => r.walkedAt },
          ]}
        />
      </Card>
    </>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  const id = `pref-${label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-ink">
        {label}
      </label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className="block w-full min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink">
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
